import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { axiosInstance, BASE_URL } from "../../Config";
import "@fancyapps/fancybox/dist/jquery.fancybox.css";
import "@fancyapps/fancybox";

const ProductRequest = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const [dateFilter, setDateFilter] = useState("all");

  const dateFilters = [
    { value: "all", label: "All Data" },
    { value: "7", label: "Last 7 Days" },
    { value: "30", label: "This Month" },
    { value: "180", label: "Last 6 Months" },
    { value: "365", label: "Last Year" },
  ];

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, dateFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (dateFilter && dateFilter !== 'all') params.append('dateFilter', dateFilter);
      if (currentPage > 1) params.append('page', currentPage);
      params.append('limit', '10');

      const queryString = params.toString();
      const url = queryString ? `/getallrequests?${queryString}` : `/getallrequests`;

      const response = await axiosInstance.get(url);

      if (response.data.success) {
        setProducts(response.data.body?.data || []);
        setTotalPages(response.data.body?.totalPages || 1);
      } else {
        setProducts([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching product requests:", error);
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleDateFilter = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  const deleteProductHandler = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "green",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axiosInstance.delete(`/delete-requests/${id}`);
        toast.success("Request deleted successfully");

        // Refresh the request list with current filters
        fetchProducts();
      } catch (error) {
        console.error("Error deleting request:", error);
        toast.error(error.response?.data?.message || "Error deleting request");
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "0" ? "1" : "0";
      await axiosInstance.post("/productstatus", {
        id,
        status: newStatus,
      });

      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === id ? { ...product, status: newStatus } : product,
        ),
      );

      if (selectedProduct && selectedProduct.id === id) {
        setSelectedProduct((prev) => ({ ...prev, status: newStatus }));
      }

      toast.success("Status updated successfully.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status.");
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct({
      ...product,
      sessions: product.sessions || [],
    });
  };

  const getCurrentFilterLabel = () => {
    const filter = dateFilters.find((f) => f.value === dateFilter);
    return filter ? filter.label : "All Data";
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const handleChatClick = (productRequest) => {
    const userData = localStorage.getItem("userData");
    if (!userData) {
      toast.error("Please login to start a chat");
      return;
    }

    let currentUser;
    try {
      currentUser = JSON.parse(userData);
    } catch (error) {
      console.error("Error parsing user data:", error);
      toast.error("Invalid user data");
      return;
    }

    // The request creator's ID - this is the person who made the product request
    // Check different possible field names based on your API response
    const requestCreatorId = productRequest.userId || 
                            productRequest.createdBy || 
                            productRequest.user?.id || 
                            productRequest.requesterId;
    
    if (!requestCreatorId) {
      console.error("Product request data:", productRequest);
      toast.error("Cannot find request creator to chat with");
      return;
    }

    if (currentUser.id === requestCreatorId) {
      toast.error("You cannot chat with yourself about your own request");
      return;
    }

    // Get request creator's name and image
    console.log("Product request data:", productRequest);
    const creatorName = productRequest.user?.name || 
                        productRequest.userName || 
                        productRequest.creatorName || 
                        productRequest.name || 
                        productRequest.User?.name ||
                        "Request Creator";
    
    const creatorImageRaw = productRequest.user?.image || 
                          productRequest.user?.imageUrl ||
                          productRequest.creatorImage || 
                          productRequest.User?.image ||
                          null;
    const creatorImage = creatorImageRaw
      ? (creatorImageRaw.startsWith('http') ? creatorImageRaw : `${BASE_URL}/${creatorImageRaw}`)
      : "https://randomuser.me/api/portraits/men/1.jpg";

    const chatData = {
      // Chat constant will be created on first message
      chatConstantId: null,
      // Current user is the sender
      senderId: currentUser.id,
      // Request creator is the receiver
      receiverId: requestCreatorId,                                                                                     
      // For display in chat list - show the request creator's info
      senderName: creatorName, // This will show the creator's name in chat list
      senderImage: creatorImage,
      // Original sender/receiver info for reference
      originalSenderId: currentUser.id,
      originalReceiverId: requestCreatorId,
      // Chat metadata
      lastMessage: "",
      timestamp: new Date().toISOString(),
      formattedTime: "Just now",
      unreadCount: 0,
      isOnline: false,
      messageType: 1,
      lastMessageSenderId: null,
      // Product request information
      productId: null,
      productRequestId: productRequest.id,
      productName: productRequest.brandName || 
                  productRequest.productName || 
                  productRequest.name || 
                  "Product Request",
      productImage: productRequest.image ? `${BASE_URL}/${productRequest.image}` : null,
      chatType: "request",
    };

    console.log("Navigating to chat with request data:", chatData);
    navigate("/chat", { state: { selectedChat: chatData } });
  };

  return (
    <>
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="title-box mb-3 pb-1 d-flex justify-content-between align-items-center">
                <h4 className="mb-0 page-title">Product Requests</h4>
              </div>
              <div>
                <nav aria-label="breadcrumb" className="mt-1">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard" className="new">
                        <i className="ri-home-4-fill me-1 new" /> Home
                      </Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/" className="new">
                        <i className="ri-group-2-line me-1 new" /> Product Requests
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Request Listings
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="card">
                <div className="card-body cusbar">
                  <div
                    className="card-head justify-content-start adjusttwo mb-3"
                    style={{ flexWrap: "nowrap" }}
                  >
                    <div className="tbl-search position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by brand, model, or user..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <i className="ri-search-line" />
                    </div>

                    <div className="dropdown tbl-drop mb-3">
                      <button
                        className="btn btn-light dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {getCurrentFilterLabel()}
                      </button>
                      <ul className="dropdown-menu">
                        {dateFilters.map((filter) => (
                          <li key={filter.value}>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleDateFilter(filter.value)}
                            >
                              {filter.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive table-card border-top">
                      <div data-simplebar="" className="cus-scroll scmob">
                        <table className="table table-centered cus-nowrap align-middle hltr mb-0">
                          <thead>
                            <tr>
                              <th>Sr no.</th>
                              {/* <th>Image</th> */}
                              <th>Requester Name</th>
                              <th>Requester Email</th>
                              <th>Brand Name</th>
                              <th>Type</th>
                              <th>Size</th>
                              <th>Model</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.length > 0 ? (
                              products.map((request, index) => (
                                <tr key={request.id}>
                                  <td>{getSerialNumber(index)}</td>
                                  {/* <td>
                                    {request.image ? (
                                      <a
                                        href={`${BASE_URL}/${request.image}`}
                                        data-fancybox="user-gallery"
                                        data-caption={
                                          request.brandName || "Product Image"
                                        }
                                        className="d-inline-block"
                                      >
                                        <img
                                          src={`${BASE_URL}/${request.image}`}
                                          alt="Product"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                              "https://via.placeholder.com/50";
                                          }}
                                        />
                                      </a>
                                    ) : (
                                      <div
                                        style={{
                                          width: "50px",
                                          height: "50px",
                                          borderRadius: "50%",
                                          backgroundColor: "#f8f9fa",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <i className="ri-image-line text-muted"></i>
                                      </div>
                                    )}
                                  </td> */} 
                                  <td>{request.user?.name || "N/A"}</td>
                                  <td>{request.user?.email || "N/A"}</td>
                                  <td>{request.brandName || "N/A"}</td>
                                  <td>{request.type || "N/A"}</td>
                                  <td>{request.size || "N/A"}</td>
                                  <td>{request.model || "N/A"}</td>
                                  <td>
                                    <div className="d-flex justify-content-end">
                                      <button
                                        type="button"
                                        className="btn btn-soft-primary px-2 btn-sm me-1"
                                        data-bs-toggle="offcanvas"
                                        data-bs-target="#view-details"
                                        aria-controls="offcanvasRight"
                                        onClick={() =>
                                          handleViewDetails(request)
                                        }
                                        title="View Details"
                                      >
                                        <i className="ri-eye-fill font-size-16"></i>
                                      </button>

                                      <button
                                        onClick={() => handleChatClick(request)}
                                        className="btn btn-soft-success px-2 btn-sm me-1"
                                        style={{
                                          backgroundColor: "#28a745",
                                          borderColor: "#28a745",
                                          color: "#fff",
                                        }}
                                        title="Start Chat"
                                      >
                                        <i className="ri-chat-3-line font-size-16"></i>
                                      </button>

                                      <button
                                        onClick={() =>
                                          deleteProductHandler(request.id)
                                        }
                                        className="btn btn-soft-danger px-2 btn-sm"
                                        style={{
                                          backgroundColor: "#ea5455",
                                          borderColor: "#ea5455",
                                          color: "#fff",
                                        }}
                                        title="Delete Request"
                                      >
                                        <i className="ri-delete-bin-line font-size-16"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center py-2">
                                  <p className="text-muted">No product requests found</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {totalPages > 1 && (
                          <div className="d-flex justify-content-center align-items-center mt-3">
                            <Stack spacing={2}>
                              <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="error"
                                showFirstButton
                                showLastButton
                              />
                            </Stack>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas for Product Request Details */}
      <div
        className="offcanvas offcanvas-end rdetails"
        tabIndex="-1"
        id="view-details"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="offcanvas-header d-block">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="offcanvas-title mb-0 fw-semibold">Product Request Details</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
        </div>
        <div className="offcanvas-body">
          {selectedProduct ? (
            <>
              <div className="scroll-room mb-3" id="scrollRoom">
                <label className="text-muted small">Image</label>
                {selectedProduct.image ? (
                  <a
                    href={`${BASE_URL}/${selectedProduct.image}`}
                    data-fancybox="gallery"
                    className="image-popup-gallery-item"
                  >
                    <img
                      src={`${BASE_URL}/${selectedProduct.image}`}
                      className="img-fluid rounded"
                      alt="product"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x300";
                      }}
                    />
                  </a>
                ) : (
                  <div
                    className="bg-light rounded d-flex align-items-center justify-content-center"
                    style={{ height: "200px" }}
                  >
                    <i className="ri-image-line font-size-48 text-muted"></i>
                  </div>
                )}
              </div>

              <div className="scroll-room mb-3" id="scrollRoom">
                <label className="text-muted small">Video</label>
                {selectedProduct.video ? (
                  <a
                    href={`${BASE_URL}/${selectedProduct.video}`}
                    data-fancybox="gallery"
                    className="image-popup-gallery-item position-relative d-block"
                  >
                    <img
                      src={`${BASE_URL}/${selectedProduct.thumbnill}`}
                      className="img-fluid rounded"
                      alt="product"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x300";
                      }}
                    />
                    <div
                      className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "rgba(0,0,0,0.6)",
                        borderRadius: "50%",
                      }}
                    >
                      <i
                        className="ri-play-fill text-white"
                        style={{ fontSize: "30px" }}
                      ></i>
                    </div>
                  </a>
                ) : (
                  <div
                    className="bg-light rounded d-flex align-items-center justify-content-center"
                    style={{ height: "200px" }}
                  >
                    <i className="ri-video-line font-size-48 text-muted"></i>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <h6 className="fw-semibold mb-3">Request Information</h6>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small">Request ID</label>
                    <p className="fw-medium">{selectedProduct.id}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Requester Name</label>
                    <p className="fw-medium">{selectedProduct.user?.name || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Requester Email</label>
                    <p className="fw-medium">{selectedProduct.user?.email || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Brand Name</label>
                    <p className="fw-medium">{selectedProduct.brandName || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Model</label>
                    <p className="fw-medium">{selectedProduct.model || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Size</label>
                    <p className="fw-medium">{selectedProduct.size || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Color</label>
                    <p className="fw-medium">{selectedProduct.color || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Type</label>
                    <p className="fw-medium">{selectedProduct.type || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Number Of Units Needed</label>
                    <p className="fw-medium">{selectedProduct.numberOfUnitsNeeded || "N/A"}</p>
                  </div>
                  {/* <div className="col-12">
                    <label className="text-muted small">Currency</label>
                    <p className="fw-medium">{selectedProduct.currency || "USD"}</p>
                  </div> */}
                  <div className="col-12">
                    <label className="text-muted small">Other Information</label>
                    <p className="fw-medium">{selectedProduct.otherinfo || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Country</label>
                    <p className="fw-medium">{selectedProduct.country || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">State</label>
                    <p className="fw-medium">{selectedProduct.state || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">City</label>
                    <p className="fw-medium">{selectedProduct.city || "N/A"}</p>
                  </div>
                  {/* <div className="col-12">
                    <label className="text-muted small">Unit Price</label>
                    <p className="fw-medium">{selectedProduct.unitPrice || "N/A"}</p>
                  </div> */}
                  <div className="col-12">
                    <label className="text-muted small">Request Delivery Date</label>
                    <p className="fw-medium">{selectedProduct.requestDeliveryDate || "N/A"}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="ri-file-list-line font-size-48 text-muted"></i>
              <p className="mt-3 text-muted">No product request selected</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductRequest;